import React, { useMemo, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { DictionaryIrregularVerb } from 'app/types';
import CategoryRail, { RailItem } from 'pages/WordSet/CategoryRail';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';
import {
  PATTERN_SUPER_GROUPS,
  getPatternLabel,
  dieslova,
} from './patternSuperGroups';
import { aggregateByPattern } from './aggregations';
import { Selection } from './urlState';
import DrillHeader from './DrillHeader';
import IrregularVerbsTable from './IrregularVerbsTable';
import PatternRow from './PatternRow';

interface Props {
  verbs: readonly DictionaryIrregularVerb[];
  selection: Selection | null;
  onSelectPattern: (pattern: string) => void;
  onClearSelection: () => void;
}

const buildPatternLink = (patternKey: string) =>
  `/wordset/irregular-verb/pattern/${encodeURIComponent(patternKey)}`;

// Single accent used everywhere on the patterns page — rail, mobile chips,
// section heading dots, and per-row hover borders + badges. Matches the
// irregular-verb brand purple shared with /wordset/level-based so the whole
// area reads as one family, not four.
const RAIL_ACCENT = '#3B2C93';

// Group head used as the in-page section anchor. Same uppercase-eyebrow look
// as the /wordset GroupHead so the layout reads as one family.
const GroupHead: React.FC<{
  color: string;
  title: string;
  count: number;
}> = ({ color, title, count }) => (
  <UIBox
    sx={{
      alignItems: 'center',
      display: 'flex',
      flexWrap: 'nowrap',
      gap: 1.5,
      mb: 1,
    }}
  >
    <UIBox
      sx={{
        bgcolor: color,
        borderRadius: '50%',
        flex: 'none',
        height: 10,
        width: 10,
      }}
    />
    <UIText
      sx={{
        color: 'primary.subtitle',
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {title}
    </UIText>
    <UIText
      sx={{
        bgcolor: 'background.default',
        borderRadius: 999,
        color: 'primary.subtitle',
        fontSize: 12,
        fontWeight: 700,
        px: 1.1,
        py: 0.25,
        whiteSpace: 'nowrap',
      }}
    >
      {`${count} ${dieslova(count)}`}
    </UIText>
    <UIBox sx={{ bgcolor: 'divider', flex: 1, height: '1px' }} />
  </UIBox>
);

const PatternsMode: React.FC<Props> = ({
  verbs,
  selection,
  onSelectPattern,
  onClearSelection,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const agg = aggregateByPattern(verbs);

  // Pre-compute each super-group's pattern list, skipping groups with no
  // data so the rail never points at an empty section. Hooks must run
  // unconditionally, so they stay above the early return below.
  const groupsWithData = useMemo(
    () =>
      PATTERN_SUPER_GROUPS.map((group) => {
        const patternsInGroup = group.patterns
          .map((p) => ({
            key: p,
            ...(agg.get(p) ?? { total: 0, learned: 0, added: 0 }),
          }))
          .filter((p) => p.total > 0)
          .sort((a, b) => b.total - a.total);
        const groupTotal = patternsInGroup.reduce((s, p) => s + p.total, 0);
        return { group, patternsInGroup, groupTotal };
      }).filter((g) => g.patternsInGroup.length > 0),
    [agg],
  );

  // CategoryRail expects RailItem[]. One synthetic group with 4 children
  // gives us the standard left-rail / mobile-chip-strip experience used on
  // /wordset — minus the redundant cluster heading (label left empty so
  // CategoryRail skips the sub-header above the links).
  const railItems = useMemo<RailItem[]>(
    () => [
      {
        kind: 'group',
        label: '',
        children: groupsWithData.map(({ group }) => ({
          id: group.slug,
          title: group.title,
          accent: RAIL_ACCENT,
        })),
      },
    ],
    [groupsWithData],
  );

  // Suppress the unused-prop lint — onSelectPattern stays in the public
  // API for parity with the other modes (Forms/Families/Levels), even
  // though Patterns now uses Link-based navigation via PatternRow.
  void onSelectPattern;

  if (selection?.kind === 'pattern') {
    const filtered = verbs.filter((v) => v.pattern === selection.value);
    return (
      <Box>
        <DrillHeader
          backLabel="До списку патернів"
          title={getPatternLabel(selection.value)}
          count={filtered.length}
          onBack={onClearSelection}
        />
        <IrregularVerbsTable verbs={filtered} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gap: { xs: 0, md: 4 },
        gridTemplateColumns: { xs: '1fr', md: '232px 1fr' },
      }}
    >
      <CategoryRail
        items={railItems}
        hoveredId={hoveredId}
        onHover={setHoveredId}
        dimInactive
      />

      <Stack spacing={3.5}>
        {groupsWithData.map(({ group, patternsInGroup, groupTotal }) => (
          <Box
            key={group.slug}
            id={`ws-${group.slug}`}
            onMouseEnter={() => setHoveredId(group.slug)}
            onMouseLeave={() => setHoveredId(null)}
            sx={{ scrollMarginTop: { xs: 128, md: 80 } }}
          >
            <GroupHead
              color={RAIL_ACCENT}
              title={group.title}
              count={groupTotal}
            />
            <Stack spacing={1}>
              {patternsInGroup.map((p, idx) => (
                <PatternRow
                  key={p.key}
                  index={idx}
                  title={getPatternLabel(p.key)}
                  total={p.total}
                  learned={p.learned}
                  added={p.added}
                  color={RAIL_ACCENT}
                  link={buildPatternLink(p.key)}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

export default PatternsMode;
